'use client';
import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { Phone, ExternalLink, MapPin, Navigation, Clock } from 'lucide-react';
import { useLocation } from '@/lib/hooks/useLocation';
import { FALLBACK_COORDS, haversineKm, getLocationState } from '@/lib/location';
import Topbar from '@/components/Topbar';
import { NS_FACILITIES } from '@/lib/data/nsFacilities';

// ─── Shared types & helpers (mirrors MapClient) ────────────────────────────────

type Volume = 'low' | 'moderate' | 'high';

const VOLUME_COLORS: Record<Volume, string> = {
  low:      '#7ebc59',
  moderate: '#f9a825',
  high:     '#d32f2f',
};

const STATUS_COLORS: Record<string, string> = {
  open:   '#7ebc59',
  busy:   '#f9a825',
  closed: '#d32f2f',
};

function formatWait(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

function computeVolume(minutes: number): Volume {
  if (minutes <= 60) return 'low';
  if (minutes <= 120) return 'moderate';
  return 'high';
}

function distKm(userLat: number, userLng: number, lat: number, lng: number) {
  return haversineKm(userLat, userLng, lat, lng);
}

function distStr(km: number): string {
  return km < 1 ? `${Math.round(km * 1000)} m away` : `${km.toFixed(1)} km away`;
}

const NS_WALKIN_STATIC = NS_FACILITIES.filter((f) => f.type === 'walkin');
const NS_PHARMACY_STATIC = NS_FACILITIES.filter((f) => f.type === 'pharmacy');

const ER_PHONES: Record<string, string> = {
  QE2: '(902) 473-2220', DGH: '(902) 465-8300', HCH: '(902) 792-2000',
  CCH: '(902) 869-6100', CRH: '(902) 893-5554', VRH: '(902) 678-7381',
  SSR: '(902) 527-5000', YRH: '(902) 742-3541', CBR: '(902) 567-8000',
  ICH: '(902) 258-2100', SMR: '(902) 863-2830', ARH: '(902) 752-7600',
  CRC: '(902) 667-3361', QGH: '(902) 354-3400',
};

// ─── Mini map component ────────────────────────────────────────────────────────

interface MapFacility {
  id: string; name: string; city: string; address: string; phone: string;
  lat: number; lng: number; type: 'er' | 'walkin' | 'pharmacy';
  wait?: string; volume?: Volume; status?: string;
  isClosed?: boolean; hasLiveData?: boolean; badge?: string;
}

function ResourcesMap({ facilities }: { facilities: MapFacility[] }) {
  const mapRef        = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const markersRef    = useRef<any[]>([]);
  const resizeObsRef  = useRef<ResizeObserver | null>(null);
  const { coords }    = useLocation();

  useEffect(() => {
    if (typeof window === 'undefined' || !mapRef.current || mapInstanceRef.current) return;

    Promise.all([import('leaflet'), import('leaflet.markercluster')]).then(([L]) => {
      if (!mapRef.current || mapInstanceRef.current) return;
      const el = mapRef.current as any;
      if (el._leaflet_id) el._leaflet_id = null;

      delete (L.Icon.Default.prototype as any)._getIconUrl;
      L.Icon.Default.mergeOptions({
        iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
        iconUrl:       'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
        shadowUrl:     'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
      });

      const { coords: initCoords } = getLocationState();
      const center = initCoords ?? FALLBACK_COORDS;

      const map = L.map(mapRef.current!, {
        center: [center.lat, center.lng], zoom: 11,
        zoomControl: true, dragging: true, scrollWheelZoom: false,
        touchZoom: true, doubleClickZoom: true,
      });
      mapInstanceRef.current = map;

      L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png', {
        attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> © <a href="https://carto.com/attributions">CARTO</a>',
        subdomains: 'abcd', maxZoom: 19,
      }).addTo(map);

      // User location marker
      const userIcon = L.divIcon({
        html: `<div style="width:14px;height:14px;background:#204998;border:3px solid white;border-radius:50%;box-shadow:0 2px 8px rgba(32,73,152,0.5);"></div>`,
        className: '', iconSize: [14, 14], iconAnchor: [7, 7],
      });
      L.marker([center.lat, center.lng], { icon: userIcon })
        .addTo(map)
        .bindPopup('<strong style="font-family:Inter,sans-serif;">Your Location</strong>');

      if (typeof ResizeObserver !== 'undefined') {
        resizeObsRef.current = new ResizeObserver(() => map.invalidateSize());
        resizeObsRef.current.observe(mapRef.current!);
      }

      requestAnimationFrame(() => map.invalidateSize());
      mapInstanceRef.current._facilityData = facilities;
      addMarkers(L, map, facilities);
    });

    return () => {
      resizeObsRef.current?.disconnect();
      if (mapInstanceRef.current) { mapInstanceRef.current.remove(); mapInstanceRef.current = null; }
    };
  }, []);

  // Update user marker when coords resolve
  useEffect(() => {
    if (!coords || !mapInstanceRef.current) return;
    mapInstanceRef.current.setView([coords.lat, coords.lng], mapInstanceRef.current.getZoom());
  }, [coords]);

  // Re-add markers when facilities load
  useEffect(() => {
    if (!mapInstanceRef.current || facilities.length === 0) return;
    import('leaflet').then((L) => {
      markersRef.current.forEach((m) => { try { mapInstanceRef.current.removeLayer(m); } catch {} });
      markersRef.current = [];
      addMarkers(L, mapInstanceRef.current, facilities);
    });
  }, [facilities]);

  function addMarkers(L: any, map: any, facs: MapFacility[]) {
    facs.forEach((fac) => {
      const color = fac.type === 'pharmacy'
        ? STATUS_COLORS[fac.status ?? 'open']
        : (fac.isClosed || fac.hasLiveData === false)
          ? '#9aa5b4'
          : VOLUME_COLORS[fac.volume ?? 'low'];

      const svg = fac.type === 'er'
        ? `<svg width="14" height="14" viewBox="0 0 24 24" fill="white"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-2 10h-4v4h-2v-4H7v-2h4V7h2v4h4v2z"/></svg>`
        : fac.type === 'walkin'
        ? `<svg width="14" height="14" viewBox="0 0 24 24" fill="white"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>`
        : `<svg width="14" height="14" viewBox="0 0 24 24" fill="white"><path d="M6 3h12v2H6zm1 3h10l1 13H5L6 6zm4 2v2H8v2h2v6h2v-6h2V10h-2V8h-2z"/></svg>`;

      const icon = L.divIcon({
        html: `<div style="width:34px;height:34px;background:${color};border:3px solid white;border-radius:50%;box-shadow:0 2px 10px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;">${svg}</div>`,
        className: '', iconSize: [34, 34], iconAnchor: [17, 17],
      });

      const waitHtml = fac.isClosed
        ? `<p style="font-size:12px;color:#9aa5b4;margin:6px 0 0;">Temporarily Closed</p>`
        : fac.hasLiveData === false
        ? `<p style="font-size:12px;color:#9aa5b4;margin:6px 0 0;">No live wait data · Call ahead</p>`
        : fac.wait
        ? `<p style="font-size:22px;font-weight:900;color:${color};font-family:'IBM Plex Mono',monospace;margin:4px 0 0;">${fac.wait}</p>`
        : '';

      const popup = `<div style="font-family:Inter,sans-serif;min-width:200px;padding:4px 0;">
        <p style="font-weight:800;color:#1a2744;margin:0 0 3px;font-size:14px;">${fac.name}</p>
        <p style="color:#6b7a99;font-size:12px;margin:0;">📍 ${fac.city}, NS</p>
        <p style="color:#6b7a99;font-size:12px;margin:0 0 2px;">📞 ${fac.phone}</p>
        ${waitHtml}
        <a href="https://www.google.com/maps/dir/?api=1&destination=${fac.lat},${fac.lng}"
           target="_blank" rel="noopener noreferrer"
           style="display:inline-block;margin-top:8px;padding:6px 14px;background:#204998;color:white;border-radius:999px;font-size:12px;font-weight:600;text-decoration:none;">
          Get Directions
        </a>
      </div>`;

      const m = L.marker([fac.lat, fac.lng], { icon }).bindPopup(popup, { maxWidth: 240 }).addTo(map);
      markersRef.current.push(m);
    });
  }

  return (
    <div ref={mapRef} style={{ width: '100%', height: 280, borderRadius: 16, overflow: 'hidden', border: '1px solid #e4eaf5' }} />
  );
}

// ─── Nearest facility card ─────────────────────────────────────────────────────

function NearestCard({ label, name, city, phone, distance, wait, color, accent }: {
  label: string; name: string; city: string; phone: string;
  distance: string; wait?: string; color: string; accent: string;
}) {
  return (
    <div className="bg-white rounded-2xl border border-[#e4eaf5] p-4">
      <p className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: accent }}>
        Nearest {label}
      </p>
      <p className="font-bold text-[#1a2744] text-base leading-tight">{name}</p>
      <p className="text-sm text-[#6b7a99] mb-3">{city}, NS</p>
      <div className="flex items-center gap-4 flex-wrap">
        <span className="flex items-center gap-1 text-sm text-[#6b7a99]">
          <Navigation size={13} className="text-[#204998]" />
          {distance}
        </span>
        {wait && (
          <span className="flex items-center gap-1 text-sm font-semibold" style={{ color }}>
            <Clock size={13} />
            {wait} wait
          </span>
        )}
        <a href={`tel:${phone}`} className="flex items-center gap-1 text-sm text-[#204998] font-medium">
          <Phone size={13} />
          {phone}
        </a>
      </div>
    </div>
  );
}

// ─── Main component ────────────────────────────────────────────────────────────

export default function ResourcesClient() {
  const { coords } = useLocation();
  const userLoc = coords ?? FALLBACK_COORDS;

  const [erFacilities, setErFacilities] = useState<MapFacility[]>([]);
  const [lastUpdated, setLastUpdated] = useState<string>('');

  useEffect(() => {
    fetch('/api/ns-ed-waittimes')
      .then((r) => r.json())
      .then((data) => {
        const ers: MapFacility[] = (data.facilities ?? []).map((f: any, i: number) => ({
          id: `er-cdn-${i}`,
          name: f.siteName,
          city: f.community,
          address: f.address ?? '',
          phone: ER_PHONES[f.siteCode] ?? '',
          lat: f.lat,
          lng: f.lng,
          type: 'er' as const,
          waitMinutes: f.isClosed ? undefined : f.waitMinutes,
          wait: f.isClosed ? undefined : formatWait(f.waitMinutes),
          volume: f.isClosed ? 'low' : computeVolume(f.waitMinutes),
          isClosed: f.isClosed,
          hasLiveData: true,
          badge: f.highCapacity ? 'HIGH CAPACITY' : undefined,
        }));
        setErFacilities(ers);
        if (data.fetchedAt) {
          setLastUpdated(new Date(data.fetchedAt).toLocaleTimeString('en-CA', { hour: '2-digit', minute: '2-digit' }));
        }
      })
      .catch(() => {});
  }, []);

  // Build the combined facility list for the map
  const walkins: MapFacility[] = NS_WALKIN_STATIC.map((w) => ({
    id: w.id, name: w.name, city: w.city, address: w.address, phone: w.phone,
    lat: w.lat, lng: w.lng, type: 'walkin' as const,
    volume: computeVolume(w.waitMinutes ?? 30),
    wait: `~${formatWait(w.waitMinutes ?? 30)}`,
    hasLiveData: false,
  }));
  const pharmacies: MapFacility[] = NS_PHARMACY_STATIC.map((p) => ({
    id: p.id, name: p.name, city: p.city, address: p.address, phone: p.phone,
    lat: p.lat, lng: p.lng, type: 'pharmacy' as const,
    volume: 'low' as Volume,
    status: (p as any).status ?? 'open',
    hasLiveData: false,
  }));
  const allFacilities: MapFacility[] = [...erFacilities, ...walkins, ...pharmacies];

  // Find nearest of each type (open, with live data preferred)
  function nearestOf(facs: MapFacility[]) {
    return [...facs]
      .filter((f) => !f.isClosed)
      .sort((a, b) => distKm(userLoc.lat, userLoc.lng, a.lat, a.lng) - distKm(userLoc.lat, userLoc.lng, b.lat, b.lng))[0];
  }

  const nearestER     = nearestOf(erFacilities.length > 0 ? erFacilities : []);
  const nearestWalkin = nearestOf(walkins);
  const nearestPharm  = nearestOf(pharmacies);

  return (
    <div className="min-h-screen bg-[#f0f4fc]">
      {/* Fixed nav wrapper — scoped to /resources to prevent scroll-over */}
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000 }}>
        <Topbar />
      </div>
      <main style={{ paddingTop: 72 }}>
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 py-8">
      <div className="space-y-6">

        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-[#1a2744]">Resources</h1>
          <p className="text-sm text-[#6b7a99] mt-1">Nearby care options, virtual care, and health line access.</p>
        </div>

        {/* Mini map */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-semibold text-[#1a2744]">Nearby Facilities</p>
            <Link href="/map" className="text-xs text-[#204998] font-semibold hover:underline">
              Open full map →
            </Link>
          </div>
          <ResourcesMap facilities={allFacilities} />
          {lastUpdated && (
            <p className="text-xs text-[#9aa5b4] mt-1.5">
              <span className="text-[#7ebc59]">●</span> ER wait times from NS Health · {lastUpdated}
            </p>
          )}
        </div>

        {/* Nearest facility cards */}
        <div className="space-y-3">
          <p className="text-sm font-bold text-[#1a2744]">Nearest to You</p>

          {nearestER && (
            <NearestCard
              label="Emergency Room"
              name={nearestER.name}
              city={nearestER.city}
              phone={nearestER.phone}
              distance={distStr(distKm(userLoc.lat, userLoc.lng, nearestER.lat, nearestER.lng))}
              wait={nearestER.wait}
              color={VOLUME_COLORS[(nearestER.volume ?? 'low') as Volume]}
              accent="#d32f2f"
            />
          )}

          {nearestWalkin && (
            <NearestCard
              label="Walk-In Clinic"
              name={nearestWalkin.name}
              city={nearestWalkin.city}
              phone={nearestWalkin.phone}
              distance={distStr(distKm(userLoc.lat, userLoc.lng, nearestWalkin.lat, nearestWalkin.lng))}
              wait={nearestWalkin.wait}
              color={VOLUME_COLORS[(nearestWalkin.volume ?? 'low') as Volume]}
              accent="#204998"
            />
          )}

          {nearestPharm && (
            <NearestCard
              label="Pharmacy"
              name={nearestPharm.name}
              city={nearestPharm.city}
              phone={nearestPharm.phone}
              distance={distStr(distKm(userLoc.lat, userLoc.lng, nearestPharm.lat, nearestPharm.lng))}
              color={STATUS_COLORS[nearestPharm.status ?? 'open']}
              accent="#0F766E"
            />
          )}
        </div>

        {/* Virtual care & helplines */}
        <div className="space-y-3">
          <p className="text-sm font-bold text-[#1a2744]">Virtual Care &amp; Help Lines</p>

          <a
            href="https://www.getmaple.ca"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-4 bg-white rounded-2xl border border-[#e4eaf5] p-4 hover:shadow-card-hover hover:border-[#c8d8f5] transition-all group"
          >
            <div className="w-12 h-12 rounded-2xl bg-[#eff3fb] flex items-center justify-center flex-shrink-0">
              <ExternalLink size={20} className="text-[#204998]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-[#1a2744]">Maple Virtual Care</p>
              <p className="text-sm text-[#6b7a99]">See a doctor or nurse by video — available 24/7, covered for most Nova Scotians.</p>
            </div>
            <span className="text-xs font-semibold text-[#204998] bg-[#eff3fb] px-3 py-1 rounded-full whitespace-nowrap">Open Maple</span>
          </a>

          <a
            href="tel:811"
            className="flex items-center gap-4 bg-white rounded-2xl border border-[#e4eaf5] p-4 hover:shadow-card-hover hover:border-[#c8d8f5] transition-all group"
          >
            <div className="w-12 h-12 rounded-2xl bg-[#f0fdfa] flex items-center justify-center flex-shrink-0">
              <Phone size={20} className="text-[#0F766E]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-[#1a2744]">811 Health Link</p>
              <p className="text-sm text-[#6b7a99]">Speak with a registered nurse 24/7. Free, confidential, available in multiple languages.</p>
            </div>
            <span className="text-xs font-semibold text-[#0F766E] bg-[#f0fdfa] px-3 py-1 rounded-full">Call 811</span>
          </a>

          <a
            href="tel:988"
            className="flex items-center gap-4 bg-white rounded-2xl border border-[#fecaca] p-4 hover:shadow-card-hover transition-all group"
          >
            <div className="w-12 h-12 rounded-2xl bg-[#fef2f2] flex items-center justify-center flex-shrink-0">
              <Phone size={20} className="text-[#d32f2f]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-[#1a2744]">988 Crisis Line</p>
              <p className="text-sm text-[#6b7a99]">Suicide &amp; crisis support — call or text 988 anytime, free and confidential.</p>
            </div>
            <span className="text-xs font-semibold text-[#d32f2f] bg-[#fef2f2] px-3 py-1 rounded-full">Call / Text</span>
          </a>
        </div>

      </div>
      </div>
      </main>
    </div>
  );
}
